"use client";

import { useTheme } from "next-themes";
import type { ComponentProps, ReactNode } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { PageWrapper } from "./PageWrapper";
import { VaraAnimatedHeader } from "./VaraAnimatedHeader";

type ProgressiveSection = {
  id: string;
  content: ReactNode;
  delay?: number;
  containerClassName?: string;
  transitionClassName?: string;
  onShow?: () => void;
};

type HeaderConfig = {
  text: string;
  fontSize?: number;
  duration?: number;
  className?: string;
  containerClassName?: string;
  wrapperClassName?: string;
  textAlign?: "left" | "center" | "right";
  showUnderline?: boolean;
  minHeight?: number;
  colors?: {
    light: string;
    dark: string;
  };
  id?: string;
};

type ProgressivePageProps = {
  pageWrapperProps?: Omit<ComponentProps<typeof PageWrapper>, "children">;
  header: HeaderConfig;
  sections: ProgressiveSection[];
  className?: string;
  contentWrapperClassName?: string;
  firstSectionDelay?: number;
  sectionDelay?: number;
};

export const ProgressivePage: React.FC<ProgressivePageProps> = ({
  pageWrapperProps,
  header,
  sections,
  className,
  contentWrapperClassName,
  firstSectionDelay = 700,
  sectionDelay = 700,
}) => {
  const { theme } = useTheme();
  const [isHeaderComplete, setIsHeaderComplete] = useState(false);
  const [visibleSectionCount, setVisibleSectionCount] = useState(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const generatedHeaderId = useId();

  const headerColors = header.colors;
  const headerColor = useMemo(() => {
    if (headerColors?.light || headerColors?.dark) {
      return theme === "dark"
        ? (headerColors?.dark ?? headerColors?.light ?? "#ffffff")
        : (headerColors?.light ?? "#111827");
    }
    return theme === "dark" ? "#ffffff" : "#111827";
  }, [headerColors, theme]);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => {
        clearTimeout(timer);
      });
      timersRef.current = [];
    };
  }, []);

  // Start first section timer once header animation completes
  useEffect(() => {
    if (!isHeaderComplete || sections.length === 0 || visibleSectionCount > 0) {
      return;
    }

    const delay = sections[0]?.delay ?? firstSectionDelay;
    const timer = setTimeout(() => {
      setVisibleSectionCount(1);
    }, delay);
    timersRef.current.push(timer);

    return () => {
      clearTimeout(timer);
      timersRef.current = timersRef.current.filter((existing) => existing !== timer);
    };
  }, [firstSectionDelay, isHeaderComplete, sections, visibleSectionCount]);

  // Reveal subsequent sections sequentially
  useEffect(() => {
    if (!isHeaderComplete) return;
    if (visibleSectionCount === 0) return;
    if (visibleSectionCount >= sections.length) return;

    const nextIndex = visibleSectionCount;
    const delay = sections[nextIndex]?.delay ?? sectionDelay;
    const timer = setTimeout(() => {
      setVisibleSectionCount((prev) => Math.min(prev + 1, sections.length));
    }, delay);
    timersRef.current.push(timer);

    return () => {
      clearTimeout(timer);
      timersRef.current = timersRef.current.filter((existing) => existing !== timer);
    };
  }, [isHeaderComplete, sectionDelay, sections, visibleSectionCount]);

  // Fire onShow callbacks as sections become visible
  const shownSectionsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!isHeaderComplete || visibleSectionCount === 0) return;

    const newlyVisible = sections.slice(0, visibleSectionCount);
    newlyVisible.forEach((section) => {
      if (!shownSectionsRef.current.has(section.id)) {
        section.onShow?.();
        shownSectionsRef.current.add(section.id);
      }
    });
  }, [isHeaderComplete, sections, visibleSectionCount]);

  return (
    <PageWrapper {...pageWrapperProps}>
      <div className={cn("w-full", className)}>
        <div className={cn("relative overflow-hidden", header.containerClassName)}>
          <div className={cn("mx-auto px-4 sm:px-6 lg:px-8 py-24", header.wrapperClassName)}>
            <div className="text-center w-full">
              <div className="w-full flex justify-center">
                <div className="w-full">
                  <VaraAnimatedHeader
                    id={header.id ?? `vara-progressive-header-${generatedHeaderId}`}
                    text={header.text}
                    fontSize={header.fontSize ?? 56}
                    color={headerColor}
                    duration={header.duration ?? 3000}
                    className={header.className ?? "mb-4"}
                    textAlign={header.textAlign ?? "center"}
                    showUnderline={header.showUnderline}
                    minHeight={header.minHeight}
                    onAnimationComplete={() => setIsHeaderComplete(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={cn("flex flex-col", contentWrapperClassName)}>
          {sections.map((section, index) => {
            const isVisible = index < visibleSectionCount;
            return (
              <div
                key={section.id}
                className={cn(
                  "transition-opacity duration-700",
                  section.containerClassName,
                  section.transitionClassName,
                  isVisible ? "opacity-100" : "opacity-0",
                )}
                aria-hidden={!isVisible}
              >
                {section.content}
              </div>
            );
          })}
        </div>
      </div>
    </PageWrapper>
  );
};
